import { faCheck, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MultiSelectDropdown } from "../components/ui";
import { UserAuth } from "../context/AuthContext";
import "../css/globals.css";
import { invalidateCache, CACHE_KEYS } from "../utils/cacheUtils";
import { classesOptions, countryOptions } from "../variables/countryOptions";

// Popular options
const popularCountries = ["US", "GB", "DE", "FR", "CN", "JP", "AU", "CA"];
const popularClasses = ["9", "25", "35", "41", "42", "43"];

const reorderOptions = (options, popularValues, isCountry = false) => {
  const popular = [];
  const rest = [];
  options.forEach(opt => {
    if (isCountry && Array.isArray(opt.value)) {
      rest.push(opt);
    } else if (popularValues.includes(opt.value)) {
      popular.push(opt);
    } else {
      rest.push(opt);
    }
  });
  popular.sort((a, b) => popularValues.indexOf(a.value) - popularValues.indexOf(b.value));
  return [...popular, ...rest];
};

const reorderedCountries = reorderOptions(countryOptions, popularCountries, true);
const shortenedClassOptions = classesOptions.map(opt => ({
  ...opt,
  label: `${opt.value}: ${opt.label.split(":")[1]?.split(";")[0]?.split(",")[0]?.trim() || opt.label}`,
}));
const reorderedClasses = reorderOptions(shortenedClassOptions, popularClasses);

// Popular countries based on actual DB usage
const POPULAR_COUNTRY_CODES = ["US", "GB", "EM", "WO", "DE", "FR", "ES", "IT", "CA", "SE", "DK", "FI"];

// Build countries from countryOptions - only individual countries (string values)
const ALL_COUNTRIES = countryOptions
  .filter(opt => typeof opt.value === "string")
  .map(opt => {
    const flag = opt.label.split(":")[0].trim();
    const name = opt.label.split(":")[1]?.trim() || opt.value;
    return {
      code: opt.value,
      name: name,
      flag: flag,
      popular: POPULAR_COUNTRY_CODES.includes(opt.value),
    };
  });

// Add special entries for WIPO and EU that aren't in the individual list
const specialEntries = [
  { code: "WO", name: "WIPO (Worldwide)", flag: "🌐", popular: true },
  { code: "EM", name: "European Union", flag: "🇪🇺", popular: true },
];
specialEntries.forEach(entry => {
  if (!ALL_COUNTRIES.find(c => c.code === entry.code)) {
    ALL_COUNTRIES.unshift(entry);
  }
});

// Popular Nice classes based on actual DB usage
const POPULAR_CLASS_CODES = [42, 35, 9, 36, 41, 32, 44];

// All 45 Nice classes with short descriptions
const ALL_NICE_CLASSES = [
  { code: 1, name: "Chemicals" },
  { code: 2, name: "Paints & Varnishes" },
  { code: 3, name: "Cosmetics & Cleaning" },
  { code: 4, name: "Lubricants & Fuels" },
  { code: 5, name: "Pharmaceuticals" },
  { code: 6, name: "Metal Goods" },
  { code: 7, name: "Machines & Tools" },
  { code: 8, name: "Hand Tools" },
  { code: 9, name: "Software & Electronics" },
  { code: 10, name: "Medical Devices" },
  { code: 11, name: "Lighting & Heating" },
  { code: 12, name: "Vehicles" },
  { code: 13, name: "Firearms & Explosives" },
  { code: 14, name: "Jewelry & Watches" },
  { code: 15, name: "Musical Instruments" },
  { code: 16, name: "Paper & Printed Matter" },
  { code: 17, name: "Rubber & Plastics" },
  { code: 18, name: "Leather Goods" },
  { code: 19, name: "Building Materials" },
  { code: 20, name: "Furniture" },
  { code: 21, name: "Household Utensils" },
  { code: 22, name: "Ropes & Textile Materials" },
  { code: 23, name: "Yarns & Threads" },
  { code: 24, name: "Textiles & Linens" },
  { code: 25, name: "Clothing & Footwear" },
  { code: 26, name: "Lace & Embroidery" },
  { code: 27, name: "Carpets & Rugs" },
  { code: 28, name: "Games & Toys" },
  { code: 29, name: "Meat & Dairy Products" },
  { code: 30, name: "Coffee, Tea & Food" },
  { code: 31, name: "Agricultural Products" },
  { code: 32, name: "Beverages (Non-Alcoholic)" },
  { code: 33, name: "Alcoholic Beverages" },
  { code: 34, name: "Tobacco" },
  { code: 35, name: "Advertising & Business" },
  { code: 36, name: "Financial Services" },
  { code: 37, name: "Construction & Repair" },
  { code: 38, name: "Telecommunications" },
  { code: 39, name: "Transport & Travel" },
  { code: 40, name: "Material Treatment" },
  { code: 41, name: "Education & Entertainment" },
  { code: 42, name: "Scientific & Tech Services" },
  { code: 43, name: "Food & Drink Services" },
  { code: 44, name: "Medical & Beauty Services" },
  { code: 45, name: "Legal & Security Services" },
].map(c => ({ ...c, popular: POPULAR_CLASS_CODES.includes(c.code) }));

const POPULAR_COUNTRIES = ALL_COUNTRIES.filter(c => c.popular);
const POPULAR_CLASSES = ALL_NICE_CLASSES.filter(c => c.popular);

// Step definitions
const STEPS = [
  { key: "name", prompt: "What should we call this project?", placeholder: "give it a name..." },
  { key: "client", prompt: data => `Who's the client?`, placeholder: "who are we working with..." },
  { key: "category", prompt: "What's the industry?", placeholder: "fintech, healthcare, lifestyle..." },
  { key: "description", prompt: "Tell us about the product", placeholder: "what makes it special...", multiline: true },
  { key: "brandStrategy", prompt: "What's the brand strategy?", placeholder: "describe positioning, target audience, brand promise...", multiline: true },
  { key: "differentiators", prompt: "Key differentiators?", placeholder: "unique, premium, innovative...", tags: true },
  { key: "attributes", prompt: "Personality traits?", placeholder: "bold, innovative, warm...", tags: true },
  { key: "maxChars", prompt: "Maximum name length?", slider: true, min: 5, max: 20 },
  { key: "countries", prompt: "Where do you need protection?", tiles: "countries" },
  { key: "niceClasses", prompt: "What industry classes?", tiles: "classes" },
  { key: "review", prompt: "Ready to create?", review: true },
];

function Form() {
  const { user } = UserAuth();
  const navigate = useNavigate();
  const { projectId } = useParams(); // For edit mode
  const host = process.env.REACT_APP_SERVER;
  const isEditMode = !!projectId;

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [, setDirection] = useState(1); // 1 = forward, -1 = back (direction used for animation)
  // Example names by character length (from DB)
  const exampleNames = {
    5: ["FARLY", "ADDAY", "AUBRA", "ENSUE", "JAIKO"],
    6: ["BRAXER", "CLATCH", "FOSSOR", "VAUGHT", "AFFOLD"],
    7: ["ARCHEAN", "AUDRILL", "AUGURAL", "FOSSICK", "GLAMARE"],
    8: ["APHORISM", "ARRIVING", "AUTHIGEN", "DATARIVE", "EARTHISM"],
    9: ["COMMOTION", "SLEEPSHIP", "ANALYCITY", "FOSSORIAL", "LATENSITY"],
    10: ["CHANDELIER", "FIRMGROUND", "FRONTIERED", "NATURALISM", "OREBRINGER"],
    11: ["CULMINATION", "FIRM GROUND", "HOLOMORPHIC", "SOUTH SENSE", "CENTER SEED"],
    12: ["FULL SURFACE", "INTELLECTION", "ROSE COMPASS", "CLEAR AS DAY", "PRECEDENTARY"],
    13: ["ARC AND DATUM", "HORIZON SOUTH", "GOLDEN FERRET", "HAPPY SPROUTS", "OFF THE BLOCK"],
    14: ["CHOPPING BLOCK", "BREAK THE BANK", "RESTLESS DREAM", "PAINT THE TOWN", "RISE AND SHINE"],
    15: ["RAPID ELOQUENCE", "THUNDER HEAVENS", "FAIR AND SQUARE", "KEY TO THE CITY", "BEHIND THE VEIL"],
    16: ["ONE FIFTEEN EAST", "NESTING INSTINCT", "GRASS IS GREENER", "MAMIHLAPINATAPAI", "BRUSSELS SPROUTS"],
    17: ["CREAM OF THE CROP", "FOUNTAIN OF YOUNG", "POTATOES TOMATOES", "SALT OF THE EARTH", "NORTHERN HORIZONS"],
    18: ["SLUMBER WITH STARS", "STORYTIME DELIGHTS", "THE DEVIL'S SESAME", "BAKED POTATO BRAIN", "STEADY AS SHE GOES"],
    19: [
      "PARALLEL THIRTY TWO",
      "STARBEAM COMPANIONS",
      "BLOOM WHERE PLANTED",
      "PROFESSIONAL PERSON",
      "BOUNDLESS WANDERERS",
    ],
    20: [
      "SERENDIPITY JUNCTION",
      "WHISPERING MOUNTAINS",
      "VELVETEEN DREAMSCAPE",
      "LUMINESCENT PATHWAYS",
      "CRYSTALLINE HORIZONS",
    ],
  };

  // Form data
  const [formData, setFormData] = useState({
    name: "",
    client: "",
    category: "",
    description: "",
    brandStrategy: "",
    differentiators: [],
    attributes: [],
    maxChars: 12,
    countries: [],
    countries1: [], // For tile comparison
    countries2: [], // For chips comparison
    countries3: [], // For typeahead comparison
    classes: [],
  });

  // Tag input state
  const [tagInput, setTagInput] = useState("");

  // Search and expand states for tile grids
  const [countrySearch, setCountrySearch] = useState("");
  const [classSearch, setClassSearch] = useState("");
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [showAllClasses, setShowAllClasses] = useState(false);

  // Ref for focusing input on step change
  const inputRef = useRef(null);

  useEffect(() => {
    if (user === null) navigate("/signin");
  }, [user, navigate]);

  // Fetch project data in edit mode
  useEffect(() => {
    if (isEditMode && projectId) {
      const fetchProject = async () => {
        try {
          const response = await fetch(`${host}/api/projects/${projectId}`);
          if (response.ok) {
            const data = await response.json();
            const project = data.data.project;
            setFormData({
              name: project.name || "",
              client: project.client || "",
              category: project.category || "",
              description: project.description || "",
              brandStrategy: project.brand_strategy || "",
              differentiators: project.differentiators || [],
              attributes: project.attributes || [],
              maxChars: project.max_chars || 12,
              countries: project.countries || [],
              classes: project.nice_classes || [],
            });
          } else {
            setError("Failed to load project");
          }
        } catch (err) {
          console.error("Error fetching project:", err);
          setError("Failed to load project");
        } finally {
          setInitialLoading(false);
        }
      };
      fetchProject();
    }
  }, [isEditMode, projectId, host]);

  // Focus input when step changes
  useEffect(() => {
    // Small delay to allow animation to start
    const timer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [step]);

  const currentStep = STEPS[step];
  const isLastStep = step === STEPS.length - 1;

  const canProceed = () => {
    if (currentStep.key === "name") return formData.name?.trim().length > 0;
    return true;
  };

  const handleNext = () => {
    if (isLastStep) {
      handleSubmit();
    } else {
      setDirection(1);
      setStep(step + 1);
      setTagInput("");
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setDirection(-1);
      setStep(step - 1);
      setTagInput("");
    }
  };

  const handleKeyDown = e => {
    if (e.key === "Enter" && !e.shiftKey && !currentStep.multiline && !currentStep.tags) {
      e.preventDefault();
      if (canProceed()) handleNext();
    }
  };

  const handleInputChange = value => {
    setFormData({ ...formData, [currentStep.key]: value });
  };

  const handleAddTag = e => {
    e.preventDefault();
    if (tagInput.trim()) {
      const currentTags = formData[currentStep.key] || [];
      // Split by comma and add each as separate tag
      const newTags = tagInput
        .split(",")
        .map(t => t.trim())
        .filter(t => t && !currentTags.includes(t));
      if (newTags.length > 0) {
        setFormData({ ...formData, [currentStep.key]: [...currentTags, ...newTags] });
      }
      setTagInput("");
    }
  };

  const handleRemoveTag = tag => {
    const currentTags = formData[currentStep.key] || [];
    setFormData({ ...formData, [currentStep.key]: currentTags.filter(t => t !== tag) });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) return;
    setLoading(true);
    setError(null);
    try {
      // Tile grid stores plain values (strings/numbers), not objects
      const countriesArray = (formData.countries || []).map(c =>
        typeof c === "object" ? (Array.isArray(c.value) ? c.value : [c.value]) : [c]
      ).flat();
      const classesArray = (formData.classes || []).map(c =>
        typeof c === "object" ? c.value : c
      );

      const payload = {
        name: formData.name,
        client: formData.client,
        category: formData.category,
        description: formData.description,
        brand_strategy: formData.brandStrategy,
        differentiators: formData.differentiators,
        attributes: formData.attributes,
        max_chars: formData.maxChars,
        countries: countriesArray,
        nice_classes: classesArray,
      };

      // Add extra fields only for create
      if (!isEditMode) {
        payload.special_chars = true;
        payload.local_spelling = "UK";
        payload.test_project = false;
        payload.status = "created";
      }

      const url = isEditMode
        ? `${host}/api/projects/${projectId}`
        : `${host}/api/projects`;

      const response = await fetch(url, {
        method: isEditMode ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        // Invalidate caches
        invalidateCache(CACHE_KEYS.projectsList());
        if (isEditMode) {
          invalidateCache(CACHE_KEYS.projectDetail(projectId));
        }

        const responseData = await response.json();
        const newProjectId = isEditMode ? projectId : responseData.data.project.id;
        setSuccess(true);
        setTimeout(() => navigate(`/projects/${newProjectId}`), 1500);
      } else {
        const errorData = await response.json().catch(() => null);
        setError(errorData?.message || `Failed to ${isEditMode ? "update" : "create"} project. Please try again.`);
      }
    } catch (err) {
      console.error("Error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!user?.email) return null;

  // Show loading while fetching project data in edit mode
  if (initialLoading) {
    return (
      <div className="zen-form-container">
        <div className="zen-gradient" />
        <div className="zen-content">
          <div className="zen-success">
            <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-[#c76f31]" />
            <p className="zen-success-text mt-4">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  const getPrompt = () => {
    // Special handling for review step in edit mode
    if (currentStep.review && isEditMode) {
      return "Ready to update?";
    }
    const prompt = currentStep.prompt;
    return typeof prompt === "function" ? prompt(formData) : prompt;
  };

  const countryValueRenderer = selected =>
    selected.length === 0 ? "Select countries..." : `${selected.length} selected`;
  const classValueRenderer = selected => (selected.length === 0 ? "Select classes..." : `${selected.length} selected`);

  return (
    <div className="zen-form-container">
      {/* Animated gradient overlay */}
      <div className="zen-gradient" />

      <div className="zen-content">
        {success ? (
          <div className="zen-success">
            <div className="zen-success-icon">
              <FontAwesomeIcon icon={faCheck} />
            </div>
            <p className="zen-success-text">{isEditMode ? "Updated" : "Created"}</p>
          </div>
        ) : (
          <>
            {/* Progress */}
            <div className="zen-progress">
              {STEPS.map((_, i) => (
                <div key={i} className={`zen-dot ${i <= step ? "active" : ""}`} />
              ))}
            </div>

            {/* Question */}
            <h2 className="zen-question" key={step}>
              {getPrompt()}
            </h2>

            {/* Input */}
            <div className="zen-input-area">
              {/* Text */}
              {!currentStep.multiline && !currentStep.tags && !currentStep.slider && !currentStep.dropdown && !currentStep.tiles && !currentStep.review && (
                <input
                  ref={inputRef}
                  type="text"
                  value={formData[currentStep.key] || ""}
                  onChange={e => handleInputChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={currentStep.placeholder}
                  className="zen-input"
                />
              )}

              {/* Textarea */}
              {currentStep.multiline && (
                <textarea
                  ref={inputRef}
                  value={formData[currentStep.key] || ""}
                  onChange={e => handleInputChange(e.target.value)}
                  placeholder={currentStep.placeholder}
                  className="zen-textarea"
                />
              )}

              {/* Tags */}
              {currentStep.tags && (
                <div className="zen-tags">
                  <form onSubmit={handleAddTag} className="zen-tag-form">
                    <input
                      ref={inputRef}
                      type="text"
                      value={tagInput}
                      onChange={e => setTagInput(e.target.value)}
                      placeholder={currentStep.placeholder}
                      className="zen-input"
                    />
                  </form>
                  {(formData[currentStep.key] || []).length > 0 && (
                    <div className="zen-tag-list">
                      {(formData[currentStep.key] || []).map((tag, idx) => (
                        <span key={idx} onClick={() => handleRemoveTag(tag)} className="zen-tag">
                          {tag}
                          <span className="zen-tag-x">×</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Slider */}
              {currentStep.slider && (
                <div className="zen-slider">
                  <div className="zen-slider-value">{formData.maxChars}</div>
                  <div className="zen-slider-track">
                    <input
                      type="range"
                      min={currentStep.min}
                      max={currentStep.max}
                      value={formData.maxChars}
                      onChange={e => handleInputChange(parseInt(e.target.value))}
                      className="zen-range"
                    />
                    <div className="zen-ticks">
                      {[5, 10, 15, 20].map(n => (
                        <div key={n} className="zen-tick" style={{ left: `${((n - 5) / 15) * 100}%` }}>
                          <div className="zen-tick-mark" />
                          <span className="zen-tick-label">{n}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="zen-slider-labels">
                    <span>Short</span>
                    <span>Long</span>
                  </div>
                  {/* Example names - pyramid layout: 1-2-2 */}
                  <div className="zen-examples">
                    {exampleNames[formData.maxChars] && (
                      <>
                        <div className="zen-examples-row">
                          <span className="zen-example-name">{exampleNames[formData.maxChars][0]}</span>
                        </div>
                        <div className="zen-examples-row">
                          <span className="zen-example-name">{exampleNames[formData.maxChars][1]}</span>
                          <span className="zen-example-name">{exampleNames[formData.maxChars][2]}</span>
                        </div>
                        <div className="zen-examples-row">
                          <span className="zen-example-name">{exampleNames[formData.maxChars][3]}</span>
                          <span className="zen-example-name">{exampleNames[formData.maxChars][4]}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Single dropdown per screen */}
              {currentStep.dropdown === "countries" && (
                <div className="zen-single-dropdown">
                  <MultiSelectDropdown
                    options={reorderedCountries}
                    value={formData.countries}
                    onChange={val => setFormData({ ...formData, countries: val })}
                    placeholder="Select countries..."
                    valueRendererFn={countryValueRenderer}
                    chipLabelFn={opt => (Array.isArray(opt.value) ? opt.label : opt.value)}
                    disableSearch
                  />
                </div>
              )}

              {currentStep.dropdown === "classes" && (
                <div className="zen-single-dropdown">
                  <MultiSelectDropdown
                    options={reorderedClasses}
                    value={formData.classes}
                    onChange={val => setFormData({ ...formData, classes: val })}
                    placeholder="Select classes..."
                    valueRendererFn={classValueRenderer}
                    chipLabelFn={opt => `${opt.value}`}
                    disableSearch
                  />
                </div>
              )}

              {/* Tile Grid for Countries */}
              {currentStep.tiles === "countries" && (
                <div className="zen-tiles">
                  <input
                    type="text"
                    value={countrySearch}
                    onChange={e => setCountrySearch(e.target.value)}
                    placeholder="Search countries..."
                    className="zen-tiles-search-input"
                  />

                  <div className="zen-tiles-grid zen-tiles-grid-countries">
                    {(countrySearch
                      ? ALL_COUNTRIES.filter(c =>
                          c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
                          c.code.toLowerCase().includes(countrySearch.toLowerCase())
                        )
                      : showAllCountries
                        ? [...POPULAR_COUNTRIES, ...ALL_COUNTRIES.filter(c => !c.popular)]
                        : POPULAR_COUNTRIES
                    ).map(item => {
                      const isSelected = (formData.countries || []).includes(item.code);
                      return (
                        <button
                          key={item.code}
                          type="button"
                          className={`zen-tile ${isSelected ? "selected" : ""}`}
                          onClick={() => {
                            const current = formData.countries || [];
                            const updated = isSelected
                              ? current.filter(c => c !== item.code)
                              : [...current, item.code];
                            setFormData({ ...formData, countries: updated });
                          }}
                          title={item.name}
                        >
                          <span className="zen-tile-flag">{item.flag}</span>
                          <span className="zen-tile-code">{item.code}</span>
                        </button>
                      );
                    })}
                  </div>

                  {!countrySearch && (
                    <button
                      type="button"
                      className="zen-tiles-toggle"
                      onClick={() => setShowAllCountries(!showAllCountries)}
                    >
                      {showAllCountries ? "− Show less" : `+ ${ALL_COUNTRIES.length - POPULAR_COUNTRIES.length} more`}
                    </button>
                  )}

                  {/* Selected items as removable chips */}
                  {(formData.countries || []).length > 0 && (
                    <div className="zen-selected-chips">
                      {(formData.countries || []).map(code => {
                        const country = ALL_COUNTRIES.find(c => c.code === code);
                        return (
                          <button
                            key={code}
                            type="button"
                            className="zen-selected-chip"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                countries: (formData.countries || []).filter(c => c !== code),
                              });
                            }}
                          >
                            {country?.flag} {code}
                            <span className="zen-chip-x">×</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Tile Grid for Nice Classes */}
              {currentStep.tiles === "classes" && (
                <div className="zen-tiles">
                  <input
                    type="text"
                    value={classSearch}
                    onChange={e => setClassSearch(e.target.value)}
                    placeholder="Search classes..."
                    className="zen-tiles-search-input"
                  />

                  <div className="zen-tiles-grid zen-tiles-grid-classes">
                    {(classSearch
                      ? ALL_NICE_CLASSES.filter(c =>
                          c.name.toLowerCase().includes(classSearch.toLowerCase()) ||
                          c.code.toString().includes(classSearch)
                        )
                      : showAllClasses
                        ? [...POPULAR_CLASSES, ...ALL_NICE_CLASSES.filter(c => !c.popular)]
                        : POPULAR_CLASSES
                    ).map(item => {
                      const isSelected = (formData.classes || []).includes(item.code);
                      return (
                        <button
                          key={item.code}
                          type="button"
                          className={`zen-tile zen-tile-class ${isSelected ? "selected" : ""}`}
                          onClick={() => {
                            const current = formData.classes || [];
                            const updated = isSelected
                              ? current.filter(c => c !== item.code)
                              : [...current, item.code];
                            setFormData({ ...formData, classes: updated });
                          }}
                        >
                          <span className="zen-tile-class-num">{item.code}</span>
                          <span className="zen-tile-class-name">{item.name}</span>
                        </button>
                      );
                    })}
                  </div>

                  {!classSearch && (
                    <button
                      type="button"
                      className="zen-tiles-toggle"
                      onClick={() => setShowAllClasses(!showAllClasses)}
                    >
                      {showAllClasses ? "− Show less" : `+ ${ALL_NICE_CLASSES.length - POPULAR_CLASSES.length} more`}
                    </button>
                  )}

                  {/* Selected items as removable chips */}
                  {(formData.classes || []).length > 0 && (
                    <div className="zen-selected-chips">
                      {(formData.classes || []).map(code => {
                        const cls = ALL_NICE_CLASSES.find(c => c.code === code);
                        return (
                          <button
                            key={code}
                            type="button"
                            className="zen-selected-chip"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                classes: (formData.classes || []).filter(c => c !== code),
                              });
                            }}
                          >
                            {code}: {cls?.name}
                            <span className="zen-chip-x">×</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Review Screen */}
              {currentStep.review && (
                <div className="zen-review">
                  <div className="zen-review-grid">
                    <div className="zen-review-item">
                      <span className="zen-review-label">Project</span>
                      <span className="zen-review-value">{formData.name || "—"}</span>
                    </div>
                    <div className="zen-review-item">
                      <span className="zen-review-label">Client</span>
                      <span className="zen-review-value">{formData.client || "—"}</span>
                    </div>
                    <div className="zen-review-item">
                      <span className="zen-review-label">Industry</span>
                      <span className="zen-review-value">{formData.category || "—"}</span>
                    </div>
                    <div className="zen-review-item">
                      <span className="zen-review-label">Max Length</span>
                      <span className="zen-review-value">{formData.maxChars} characters</span>
                    </div>
                  </div>

                  {formData.description && (
                    <div className="zen-review-block">
                      <span className="zen-review-label">Description</span>
                      <p className="zen-review-text">{formData.description}</p>
                    </div>
                  )}

                  {formData.brandStrategy && (
                    <div className="zen-review-block">
                      <span className="zen-review-label">Brand Strategy</span>
                      <p className="zen-review-text">{formData.brandStrategy}</p>
                    </div>
                  )}

                  {(formData.differentiators || []).length > 0 && (
                    <div className="zen-review-block">
                      <span className="zen-review-label">Differentiators</span>
                      <div className="zen-review-tags">
                        {formData.differentiators.map((diff, i) => (
                          <span key={i} className="zen-review-tag">{diff}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {(formData.attributes || []).length > 0 && (
                    <div className="zen-review-block">
                      <span className="zen-review-label">Attributes</span>
                      <div className="zen-review-tags">
                        {formData.attributes.map((attr, i) => (
                          <span key={i} className="zen-review-tag">{attr}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {(formData.countries || []).length > 0 && (
                    <div className="zen-review-block">
                      <span className="zen-review-label">Countries ({formData.countries.length})</span>
                      <div className="zen-review-tags">
                        {formData.countries.map(code => {
                          const country = ALL_COUNTRIES.find(c => c.code === code);
                          return (
                            <span key={code} className="zen-review-tag">{country?.flag} {code}</span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {(formData.classes || []).length > 0 && (
                    <div className="zen-review-block">
                      <span className="zen-review-label">Classes ({formData.classes.length})</span>
                      <div className="zen-review-tags">
                        {formData.classes.map(code => (
                          <span key={code} className="zen-review-tag">{code}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* OPTION 2: Inline Chips */}
              {currentStep.chips && (
                <div className="zen-chips">
                  <div className="zen-chips-wrap">
                    {POPULAR_COUNTRIES.map(country => {
                      const isSelected = (formData[currentStep.key] || []).includes(country.code);
                      return (
                        <button
                          key={country.code}
                          type="button"
                          className={`zen-chip ${isSelected ? "selected" : ""}`}
                          onClick={() => {
                            const current = formData[currentStep.key] || [];
                            const updated = isSelected
                              ? current.filter(c => c !== country.code)
                              : [...current, country.code];
                            setFormData({ ...formData, [currentStep.key]: updated });
                          }}
                        >
                          {country.flag} {country.code}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* OPTION 3: Type to Add */}
              {currentStep.typeahead && (
                <div className="zen-typeahead">
                  <input
                    ref={inputRef}
                    type="text"
                    value={countrySearch}
                    onChange={e => setCountrySearch(e.target.value)}
                    placeholder={currentStep.placeholder}
                    className="zen-input"
                  />
                  {countrySearch && (
                    <div className="zen-typeahead-results">
                      {POPULAR_COUNTRIES
                        .filter(c =>
                          c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
                          c.code.toLowerCase().includes(countrySearch.toLowerCase())
                        )
                        .slice(0, 6)
                        .map(country => {
                          const isSelected = (formData[currentStep.key] || []).includes(country.code);
                          return (
                            <button
                              key={country.code}
                              type="button"
                              className={`zen-typeahead-item ${isSelected ? "selected" : ""}`}
                              onClick={() => {
                                const current = formData[currentStep.key] || [];
                                if (!isSelected) {
                                  setFormData({ ...formData, [currentStep.key]: [...current, country.code] });
                                }
                                setCountrySearch("");
                              }}
                            >
                              {country.flag} {country.name}
                              {isSelected && <span className="zen-check">✓</span>}
                            </button>
                          );
                        })}
                    </div>
                  )}
                  {(formData[currentStep.key] || []).length > 0 && (
                    <div className="zen-typeahead-selected">
                      {(formData[currentStep.key] || []).map(code => {
                        const country = POPULAR_COUNTRIES.find(c => c.code === code);
                        return (
                          <span
                            key={code}
                            className="zen-tag"
                            onClick={() => {
                              const current = formData[currentStep.key] || [];
                              setFormData({ ...formData, [currentStep.key]: current.filter(c => c !== code) });
                            }}
                          >
                            {country?.flag} {code}
                            <span className="zen-tag-x">×</span>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Error message */}
            {error && (
              <div className="zen-error">
                {error}
              </div>
            )}

            {/* Navigation */}
            <div className="zen-nav">
              {step === 0 ? (
                <div className="relative">
                  <button onClick={() => navigate(isEditMode ? `/projects/${projectId}` : '/projects')} className="zen-nav-btn zen-back">
                    <svg width="24" height="14" viewBox="0 0 81.153 43.501" className="zen-swoosh-icon zen-swoosh-left">
                      <path
                        d="M40.577,36.827C36.648,15.416,14.731,0,0,0V1.349c15.7,0,39.9,18.1,39.9,42.152h1.349c0-24.055,24.206-42.152,39.9-42.152V0C66.423,0,44.5,15.416,40.577,36.827"
                        fill="currentColor"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                    </svg>
                  </button>
                  <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 text-[10px] text-base-content/30">Exit</span>
                </div>
              ) : (
                <button onClick={handleBack} className="zen-nav-btn zen-back">
                  <svg width="24" height="14" viewBox="0 0 81.153 43.501" className="zen-swoosh-icon zen-swoosh-left">
                    <path
                      d="M40.577,36.827C36.648,15.416,14.731,0,0,0V1.349c15.7,0,39.9,18.1,39.9,42.152h1.349c0-24.055,24.206-42.152,39.9-42.152V0C66.423,0,44.5,15.416,40.577,36.827"
                      fill="currentColor"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                  </svg>
                </button>
              )}
              <div className="zen-nav-spacer" />
              <button onClick={handleNext} disabled={!canProceed() || loading} className="zen-nav-btn zen-next">
                {loading ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : isLastStep ? (
                  <svg width="24" height="14" viewBox="0 0 81.153 43.501" className="zen-swoosh-icon zen-swoosh-down">
                    <path
                      d="M40.577,36.827C36.648,15.416,14.731,0,0,0V1.349c15.7,0,39.9,18.1,39.9,42.152h1.349c0-24.055,24.206-42.152,39.9-42.152V0C66.423,0,44.5,15.416,40.577,36.827"
                      fill="currentColor"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                  </svg>
                ) : (
                  <svg width="24" height="14" viewBox="0 0 81.153 43.501" className="zen-swoosh-icon zen-swoosh-right">
                    <path
                      d="M40.577,36.827C36.648,15.416,14.731,0,0,0V1.349c15.7,0,39.9,18.1,39.9,42.152h1.349c0-24.055,24.206-42.152,39.9-42.152V0C66.423,0,44.5,15.416,40.577,36.827"
                      fill="currentColor"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                  </svg>
                )}
              </button>
            </div>
          </>
        )}
      </div>

      <style>{`
        .zen-form-container {
          position: fixed;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(160deg, #f5f0e8 0%, #ebe6de 50%, #f0ebe3 100%);
          overflow: hidden;
        }

        .zen-gradient {
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse at 50% 30%, rgba(199, 111, 49, 0.08), transparent 60%);
          animation: breathe 10s ease-in-out infinite;
          pointer-events: none;
        }

        @keyframes breathe {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }

        .zen-content {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 580px;
          padding: 2rem;
          text-align: center;
        }

        .zen-progress {
          display: flex;
          justify-content: center;
          gap: 8px;
          margin-bottom: 4rem;
        }

        .zen-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.15);
          transition: all 0.5s ease;
        }

        .zen-dot.active {
          background: #c76f31;
        }

        .zen-question {
          font-family: 'Arizona_Flare_Thin', Georgia, serif;
          font-size: 1.75rem;
          font-weight: 300;
          color: #333;
          margin: 0 0 3rem 0;
          letter-spacing: -0.02em;
          animation: fadeUp 0.5s ease;
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .zen-input-area {
          min-height: 120px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          animation: fadeUp 0.5s ease 0.1s both;
        }

        .zen-input {
          width: 100%;
          background: transparent;
          border: none;
          border-bottom: 1px solid rgba(0, 0, 0, 0.1);
          padding: 1.25rem 0;
          font-family: 'Arizona_Flare_Thin', serif;
          font-size: 1.35rem;
          letter-spacing: 0.01em;
          color: #2a2a2a;
          text-align: center;
          outline: none;
          transition: all 0.4s ease;
          caret-color: #c76f31;
        }

        .zen-input::placeholder {
          color: rgba(0, 0, 0, 0.25);
        }

        .zen-input:focus {
          border-bottom-color: #c76f31;
          border-bottom-width: 2px;
          margin-bottom: -1px;
        }

        .zen-textarea {
          width: 100%;
          background: transparent;
          border: none;
          border-bottom: 1px solid rgba(0, 0, 0, 0.1);
          padding: 1.25rem 0;
          font-family: 'Arizona_Flare_Thin', serif;
          font-size: 1.5rem;
          letter-spacing: 0.01em;
          line-height: 1.5;
          color: #2a2a2a;
          text-align: center;
          outline: none;
          resize: none;
          min-height: 100px;
          transition: all 0.4s ease;
          caret-color: #c76f31;
        }

        .zen-textarea:focus {
          border-bottom-color: #c76f31;
          border-bottom-width: 2px;
          margin-bottom: -1px;
        }

        .zen-textarea::placeholder {
          color: rgba(0, 0, 0, 0.25);
        }

        .zen-tags {
          width: 100%;
        }

        .zen-tag-form {
          width: 100%;
        }

        .zen-tag-list {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 10px;
          margin-top: 1.5rem;
        }

        .zen-tag {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: transparent;
          color: rgba(0, 0, 0, 0.5);
          border: 1px solid rgba(0, 0, 0, 0.15);
          border-radius: 4px;
          font-size: 0.8rem;
          font-family: 'Apercu_Light', system-ui, sans-serif;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .zen-tag:hover {
          border-color: #c76f31;
          color: #c76f31;
        }

        .zen-tag-x {
          font-size: 1rem;
          line-height: 1;
          opacity: 0.4;
          margin-left: 2px;
        }

        .zen-tag:hover .zen-tag-x {
          opacity: 1;
        }

        .zen-slider {
          width: 100%;
          max-width: 280px;
        }

        .zen-slider-value {
          font-family: 'GT-Pressura-Mono', 'SF Mono', 'Monaco', monospace;
          font-size: 3.5rem;
          color: rgba(0, 0, 0, 0.55);
          margin-bottom: 1.5rem;
          font-weight: 400;
        }

        .zen-slider-track {
          position: relative;
          width: 100%;
        }

        .zen-ticks {
          position: absolute;
          top: 10px;
          left: 9px;
          right: 9px;
          z-index: 0;
        }

        .zen-tick {
          position: absolute;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .zen-tick-mark {
          width: 1px;
          height: 8px;
          background: rgba(0, 0, 0, 0.12);
        }

        .zen-tick-label {
          font-family: 'GT-Pressura-Mono', monospace;
          font-size: 0.6rem;
          color: rgba(0, 0, 0, 0.2);
          margin-top: 8px;
        }

        .zen-range {
          position: relative;
          z-index: 1;
          width: 100%;
          height: 2px;
          background: rgba(0, 0, 0, 0.1);
          border-radius: 2px;
          outline: none;
          -webkit-appearance: none;
          appearance: none;
        }

        .zen-range::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 18px;
          height: 18px;
          background: #c76f31;
          border-radius: 50%;
          cursor: pointer;
          transition: transform 0.2s ease;
        }

        .zen-range::-webkit-slider-thumb:hover {
          transform: scale(1.2);
        }

        .zen-slider-labels {
          display: flex;
          justify-content: space-between;
          margin-top: 2.5rem;
          font-size: 0.7rem;
          color: rgba(0, 0, 0, 0.3);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .zen-examples {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          margin-top: 2.5rem;
          min-height: 90px;
        }

        .zen-examples-row {
          display: flex;
          justify-content: center;
          gap: 20px;
        }

        .zen-example-name {
          font-family: 'Oswald', sans-serif;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: -0.02em;
          font-size: 0.9rem;
          color: rgba(0, 0, 0, 0.45);
          transform: skewX(-6deg);
          white-space: nowrap;
        }

        .zen-single-dropdown {
          width: 100%;
          position: relative;
          animation: fadeUp 0.5s ease 0.1s both;
        }

        .zen-single-dropdown .multi-select-custom .dropdown-content {
          position: fixed !important;
          left: 50% !important;
          top: 50% !important;
          transform: translate(-50%, -50%) !important;
          width: 90vw !important;
          max-width: 400px !important;
          max-height: 60vh !important;
          z-index: 9999 !important;
          background: #fff !important;
          border-radius: 16px !important;
          border: 1px solid rgba(0, 0, 0, 0.08) !important;
          /* Massive shadow acts as backdrop */
          box-shadow: 0 0 0 100vmax rgba(245, 242, 238, 0.97), 0 20px 60px rgba(0, 0, 0, 0.12) !important;
        }

        /* Dropdown items - normal case */
        .zen-single-dropdown .multi-select-custom .select-item,
        .zen-single-dropdown .multi-select-custom .select-item label,
        .zen-single-dropdown .multi-select-custom .select-item span {
          text-transform: none !important;
        }

        /* Selected chips - centered */
        .zen-single-dropdown .flex.flex-wrap {
          justify-content: center;
          margin-top: 1.5rem !important;
        }

        /* ===== TILE GRID ===== */
        .zen-tiles {
          animation: fadeUp 0.5s ease 0.1s both;
          width: 100%;
        }

        .zen-tiles-search-input {
          width: 100%;
          padding: 0.5rem 0;
          margin-bottom: 1rem;
          background: transparent;
          border: none;
          border-bottom: 1px solid rgba(0, 0, 0, 0.1);
          font-size: 0.85rem;
          text-align: center;
          outline: none;
          transition: border-color 0.3s ease;
        }

        .zen-tiles-search-input:focus {
          border-bottom-color: #c76f31;
        }

        .zen-tiles-search-input::placeholder {
          color: rgba(0, 0, 0, 0.3);
        }

        .zen-tiles-grid {
          display: grid;
          gap: 8px;
          margin-bottom: 0.75rem;
          max-height: 45vh;
          overflow-y: auto;
          padding: 2px;
        }

        .zen-tiles-grid-countries {
          grid-template-columns: repeat(6, 1fr);
        }

        .zen-tile {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 0.5rem 0.25rem;
          background: transparent;
          border: 1px solid rgba(0, 0, 0, 0.08);
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .zen-tile:hover {
          border-color: rgba(0, 0, 0, 0.2);
          background: rgba(0, 0, 0, 0.02);
        }

        .zen-tile.selected {
          border-color: #c76f31;
          background: rgba(199, 111, 49, 0.1);
        }

        .zen-tile-flag {
          font-size: 1.5rem;
          line-height: 1;
        }

        .zen-tile-code {
          font-size: 0.6rem;
          font-weight: 600;
          color: rgba(0, 0, 0, 0.4);
          letter-spacing: 0.03em;
          margin-top: 2px;
        }

        .zen-tile.selected .zen-tile-code {
          color: #c76f31;
        }

        .zen-tiles-toggle {
          display: block;
          width: 100%;
          padding: 0.5rem;
          background: transparent;
          border: none;
          color: rgba(0, 0, 0, 0.35);
          font-size: 0.7rem;
          cursor: pointer;
          transition: color 0.2s ease;
        }

        .zen-tiles-toggle:hover {
          color: #c76f31;
        }

        /* Selected chips below grid */
        .zen-selected-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid rgba(0, 0, 0, 0.06);
          justify-content: center;
        }

        .zen-selected-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 0.35rem 0.6rem;
          background: rgba(199, 111, 49, 0.1);
          border: 1px solid rgba(199, 111, 49, 0.3);
          border-radius: 100px;
          font-size: 0.7rem;
          color: #c76f31;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .zen-selected-chip:hover {
          background: rgba(199, 111, 49, 0.2);
          border-color: #c76f31;
        }

        .zen-chip-x {
          font-size: 0.85rem;
          line-height: 1;
          opacity: 0.6;
        }

        .zen-selected-chip:hover .zen-chip-x {
          opacity: 1;
        }

        /* Nice Classes grid - 3 columns, compact */
        .zen-tiles-grid-classes {
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
        }

        .zen-tile-class {
          flex-direction: row;
          gap: 8px;
          padding: 0.6rem 0.75rem;
          justify-content: flex-start;
        }

        .zen-tile-class-num {
          font-family: 'GT-Pressura-Mono', 'SF Mono', monospace;
          font-size: 0.9rem;
          font-weight: 600;
          color: rgba(0, 0, 0, 0.25);
          min-width: 22px;
        }

        .zen-tile-class.selected .zen-tile-class-num {
          color: #c76f31;
        }

        .zen-tile-class-name {
          font-size: 0.65rem;
          color: rgba(0, 0, 0, 0.5);
          text-align: left;
          line-height: 1.2;
        }

        .zen-tile-class.selected .zen-tile-class-name {
          color: rgba(0, 0, 0, 0.7);
        }

        /* ===== REVIEW SCREEN ===== */
        .zen-review {
          animation: fadeUp 0.5s ease 0.1s both;
          width: 100%;
          text-align: left;
        }

        .zen-review-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .zen-review-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .zen-review-label {
          font-size: 0.6rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: rgba(0, 0, 0, 0.35);
        }

        .zen-review-value {
          font-size: 0.9rem;
          color: rgba(0, 0, 0, 0.7);
        }

        .zen-review-block {
          margin-bottom: 1.25rem;
          padding-top: 1rem;
          border-top: 1px solid rgba(0, 0, 0, 0.06);
        }

        .zen-review-text {
          font-size: 0.85rem;
          color: rgba(0, 0, 0, 0.6);
          line-height: 1.5;
          margin: 0.5rem 0 0 0;
        }

        .zen-review-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 0.5rem;
        }

        .zen-review-tag {
          display: inline-block;
          padding: 0.3rem 0.6rem;
          background: rgba(0, 0, 0, 0.04);
          border-radius: 4px;
          font-size: 0.7rem;
          color: rgba(0, 0, 0, 0.6);
        }

        /* ===== OPTION 2: INLINE CHIPS ===== */
        .zen-chips {
          animation: fadeUp 0.5s ease 0.1s both;
        }

        .zen-chips-wrap {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 10px;
        }

        .zen-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 0.6rem 1rem;
          background: transparent;
          border: 1px solid rgba(0, 0, 0, 0.12);
          border-radius: 100px;
          font-size: 0.85rem;
          color: rgba(0, 0, 0, 0.6);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .zen-chip:hover {
          border-color: rgba(0, 0, 0, 0.25);
        }

        .zen-chip.selected {
          border-color: #c76f31;
          background: rgba(199, 111, 49, 0.1);
          color: #c76f31;
        }

        /* ===== OPTION 3: TYPEAHEAD ===== */
        .zen-typeahead {
          width: 100%;
          animation: fadeUp 0.5s ease 0.1s both;
        }

        .zen-typeahead-results {
          margin-top: 1rem;
          background: #fff;
          border: 1px solid rgba(0, 0, 0, 0.08);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
        }

        .zen-typeahead-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 0.75rem 1rem;
          background: transparent;
          border: none;
          border-bottom: 1px solid rgba(0, 0, 0, 0.05);
          font-size: 0.9rem;
          color: rgba(0, 0, 0, 0.7);
          cursor: pointer;
          text-align: left;
          transition: background 0.15s ease;
        }

        .zen-typeahead-item:last-child {
          border-bottom: none;
        }

        .zen-typeahead-item:hover {
          background: rgba(199, 111, 49, 0.05);
        }

        .zen-typeahead-item.selected {
          background: rgba(199, 111, 49, 0.08);
          color: #c76f31;
        }

        .zen-check {
          color: #c76f31;
          font-weight: bold;
        }

        .zen-typeahead-selected {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 10px;
          margin-top: 1.5rem;
        }

        .zen-error {
          text-align: center;
          color: #c76f31;
          font-size: 0.85rem;
          padding: 0.75rem 1rem;
          background: rgba(199, 111, 49, 0.08);
          border-radius: 6px;
          margin-top: 1rem;
          animation: fadeUp 0.3s ease;
        }

        .zen-swoosh-icon {
          display: block;
        }

        .zen-swoosh-left {
          transform: rotate(90deg);
        }

        .zen-swoosh-right {
          transform: rotate(-90deg);
        }

        .zen-swoosh-down {
          transform: rotate(180deg);
        }

        .zen-nav {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-top: 4rem;
          gap: 1rem;
          animation: fadeUp 0.5s ease 0.2s both;
        }

        .zen-nav-spacer {
          flex: 1;
        }

        .zen-nav-btn {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          border: none;
          background: transparent;
          color: rgba(0, 0, 0, 0.3);
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1rem;
          outline: none;
          box-shadow: none;
        }

        .zen-nav-btn:hover:not(:disabled) {
          color: #c76f31;
          background: rgba(199, 111, 49, 0.1);
        }

        .zen-nav-btn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .zen-next {
          background: rgba(199, 111, 49, 0.1);
          color: #c76f31;
        }

        .zen-next:hover:not(:disabled) {
          background: #c76f31;
          color: white;
        }

        .zen-success {
          animation: fadeUp 0.5s ease;
        }

        .zen-success-icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: rgba(199, 111, 49, 0.1);
          color: #c76f31;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          margin: 0 auto 1.5rem;
        }

        .zen-success-text {
          font-family: 'Arizona_Flare_Thin', Georgia, serif;
          font-size: 1.5rem;
          color: #333;
          margin: 0;
        }
      `}</style>
    </div>
  );
}

export default Form;
